import {
  ARCHIVE_USER_SUCCESS,
  GET_USERS_SUCCESS,
  REVOKE_INVITE_SUCCESS,
  UPDATE_USER_SUCCESS,
} from 'actions';
import { EntityType, Role, UserFeatures, UserState } from 'enums';
import { Model, ModelType, attr } from 'redux-orm';

interface IUser {
  id: string;
  /**
   * The type of entity (Company, User or Contact)
   */
  entityType: EntityType;
  accountId: string;
  /**
   * Users first name
   */
  firstName: string;
  /**
   * Users last name
   */
  lastName: string;
  /**
   * Users full name (generated in the Model combining First + Last names together
   */
  name: string;
  /**
   * The main email associated with the user
   */
  email: string;
  /**
   * An array of email accounts associated with the user
   */
  emails: string[];
  /**
   * Invited users have an expiry date
   */
  expiry?: string;
  /**
   * An array of email accounts associated with the user
   */
  phone: string[];
  /**
   * Timestamp of the last time notifications were read
   */
  notificationsLastRead: string;
  /**
   * A flag indicating if the contact has been archived
   */
  isArchived: boolean;
  /**
   * Indicates the state of the user account
   */
  state: UserState;
  /**
   * The timezone the user is in
   */
  timezone: string;
  /**
   * The roles this user is a member of
   */
  roles: Role[];
  features: UserFeatures[];
  /**
   * User specific configuration
   */
  config: {
    o365: {
      calendarSync: {
        enabled: boolean;
      };
      emailSync: {
        enabled: boolean;
      };
    };
  };
}

class User extends Model<typeof User, IUser> {
  firstName!: string;

  lastName!: string;

  name!: string;

  email!: string;

  phone!: string;

  static reducer(action: any, model: ModelType<User>) {
    // eslint-disable-next-line default-case
    switch (action.type) {
      case GET_USERS_SUCCESS:
        // Prior to updating the list we need to remove the pending users (as they may have accepted)
        model
          .all()
          .filter((user) => user.state === 'invited')
          .delete();

        action.data.forEach((user: Partial<IUser> & { id: string }) => {
          const current = model.withId(user.id);
          if (!current) {
            model.upsert({
              // default config settings
              config: {
                o365: {
                  calendarSync: {
                    enabled: true,
                  },
                  emailSync: {
                    enabled: true,
                  },
                },
              },
              ...user,
            });
          }
          // Only update an existing user if it is not already archived
          else if (!current.isArchived) {
            model.upsert(user);
          }
        });
        break;

      case UPDATE_USER_SUCCESS:
        model.upsert(action.data);
        break;

      case ARCHIVE_USER_SUCCESS: {
        // Note we dont use delete() as we want to keep the archived data in the orm store
        const user = model.withId(action.data);
        const archive = { isArchived: true };
        user?.update(archive);
        break;
      }

      case REVOKE_INVITE_SUCCESS: {
        const current = model.withId(action.data);
        current?.delete();
        break;
      }
    }

    return undefined;
  }

  toString() {
    if (this.firstName) {
      return [this.firstName, this.lastName].join(' ');
    }
    return this.email;
  }

  toViewModel() {
    return {
      ...this.ref,
      name: this.toString(),
      entityType: EntityType.USER,
    };
  }
}

User.modelName = 'User';

User.fields = {
  id: attr(),
  accountId: attr(),
  email: attr(),
  firstName: attr(),
  lastName: attr(),
  emails: attr(),
  phone: attr(),
  notificationsLastRead: attr(),
  isArchived: attr(),
  state: attr(),
};

export type UserViewModel = ReturnType<User['toViewModel']>;

export default User;
